import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Paper, Select, MenuItem, InputLabel, FormControl, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { apiRequest } from '../../../utils/fetch';
import { SystemNotificationInterface, NotificationType, GenericResponseInterface } from '../../../interfaces/interfaces';

const NOTIFICATION_TYPES: NotificationType[] = [
  "owner_deactivated", "refund_issued", "support_started", "message_received", "admin_info", "system_alert",
  "adspace_cancelled", "adspace_reactivated", "payment_failed", "payment_success", "payment_received",
  "payment_refunded", "account_warning", "account_suspended", "info", "user_response"
];

const initialNotification: Partial<SystemNotificationInterface> = {
  adSpaceId: undefined,
  notificationType: '' as NotificationType,
  notificationMessage: '',
  threadId: '',
  active: true,
  metaData: '',
  recipientUserId: undefined,
  fromUserId: undefined,
  broadcast: false,
};

const HELP_TEXT = `
Insert modes:
• With threadId: Bind this notification to a specific thread (conversation).
• Without threadId: A new threadId will be generated (start a new conversation).
• Broadcast: Send to all users (recipientUserId not required).
`;

const SystemNotificationsAdminAddSystemNotificationPage: React.FC = () => {
  const [notification, setNotification] = useState<Partial<SystemNotificationInterface>>(initialNotification);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleChange = (field: keyof SystemNotificationInterface, value: any) => {
    setNotification(n => ({ ...n, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Parse metaData if it's a string
    let metaDataValue = notification.metaData;
    if (typeof metaDataValue === 'string' && metaDataValue.trim() !== '') {
      try {
        metaDataValue = JSON.parse(metaDataValue);
      } catch {
        setError('Meta Data must be valid JSON');
        setDialogOpen(true);
        return;
      }
    }

    try {
      const result = await apiRequest<GenericResponseInterface<SystemNotificationInterface>>('/systemNotifications/insert', 'POST', { ...notification, metaData: metaDataValue });
      if (result.status) {
        setSuccess(result.message || 'System notification created successfully!');
        setDialogOpen(true);
        setNotification(initialNotification);
      } else {
        setError(result.message || 'Failed to create system notification');
        setDialogOpen(true);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create system notification');
      setDialogOpen(true);
    }
  };

  return (
    <Paper sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="h5" gutterBottom>Add New System Notification</Typography>
        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{HELP_TEXT}</span>} arrow>
          <IconButton size="small" sx={{ ml: 1 }}>
            <InfoOutlinedIcon />
          </IconButton>
        </Tooltip>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      <form onSubmit={handleSubmit}>
        <TextField
          label="Ad Space ID"
          type="number"
          value={notification.adSpaceId ?? ''}
          onChange={e => handleChange('adSpaceId', e.target.value ? Number(e.target.value) : undefined)}
          fullWidth
          margin="dense"
        />
        <FormControl fullWidth margin="dense" required>
          <InputLabel id="notification-type-label">Notification Type</InputLabel>
          <Select
            labelId="notification-type-label"
            value={notification.notificationType ?? ''}
            label="Notification Type"
            onChange={e => handleChange('notificationType', e.target.value)}
          >
            {NOTIFICATION_TYPES.map(type => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Notification Message"
          value={notification.notificationMessage ?? ''}
          onChange={e => handleChange('notificationMessage', e.target.value)}
          fullWidth
          margin="dense"
          required
        />
        <TextField
          label="Thread ID"
          value={notification.threadId ?? ''}
          onChange={e => handleChange('threadId', e.target.value)}
          fullWidth
          margin="dense"
          helperText="Leave empty to auto-generate"
        />
        <TextField
          label="Recipient User ID"
          type="number"
          value={notification.recipientUserId ?? ''}
          onChange={e => handleChange('recipientUserId', e.target.value ? Number(e.target.value) : undefined)}
          fullWidth
          margin="dense"
          disabled={notification.broadcast === true}
        />
        <TextField
          label="From User ID"
          type="number"
          value={notification.fromUserId ?? ''}
          onChange={e => handleChange('fromUserId', e.target.value ? Number(e.target.value) : undefined)}
          fullWidth
          margin="dense"
        />
        <FormControl fullWidth margin="dense">
          <InputLabel id="broadcast-label">Broadcast</InputLabel>
          <Select
            labelId="broadcast-label"
            value={notification.broadcast ? 'yes' : 'no'}
            label="Broadcast"
            onChange={e => handleChange('broadcast', e.target.value === 'yes')}
          >
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth margin="dense">
          <InputLabel id="active-label">Active</InputLabel>
          <Select
            labelId="active-label"
            value={notification.active ? 'yes' : 'no'}
            label="Active"
            onChange={e => handleChange('active', e.target.value === 'yes')}
          >
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Meta Data (JSON)"
          value={typeof notification.metaData === 'string' ? notification.metaData : JSON.stringify(notification.metaData ?? '')}
          onChange={e => handleChange('metaData', e.target.value)}
          fullWidth
          margin="dense"
          multiline
          minRows={2}
        />
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="submit" variant="contained" color="primary">
            Create System Notification
          </Button>
        </Box>
      </form>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{success ? 'Success' : 'Error'}</DialogTitle>
        <DialogContent>
          <Typography>{success || error}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default SystemNotificationsAdminAddSystemNotificationPage;